import React, { useState, useEffect } from 'react';
import {
  Box, Button, IconButton, Typography, Stack, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import Swal from 'sweetalert2';
import { Plus, Trash2, Layers } from 'lucide-react';
import { PageHeader, StatusChip, Panel, StatStrip, LoadGate, PageShell, DIALOG_PAPER_SX } from './businessUi';
import { formatNumber } from './erpData';
import { useErp } from './useErp';
import { ERP_KEYS } from '../../Services/app/erpservice';

const KEYS = { boms: ERP_KEYS.boms, inventory: ERP_KEYS.inventory };

// Dialog to create one Bill of Materials: a finished good header + N components.
function BomDialog({ open, onClose, onSave, inventory, existingCodes }) {
  const hasItems = inventory.length > 0;
  const [fgCode, setFgCode] = useState('');
  const [fgName, setFgName] = useState('');
  const [fgUom, setFgUom] = useState('nos');
  const [rows, setRows] = useState([{ code: '', qtyPer: 1 }]);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setFgCode(''); setFgName(''); setFgUom('nos');
      setRows([{ code: '', qtyPer: 1 }]); setErr('');
    }
  }, [open]);

  const itemByCode = Object.fromEntries(inventory.map((i) => [i.code, i]));

  const setRow = (idx, patch) => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, { code: '', qtyPer: 1 }]);
  const removeRow = (idx) => setRows((rs) => (rs.length > 1 ? rs.filter((_, i) => i !== idx) : rs));

  const submit = () => {
    if (!fgCode.trim()) return setErr('Finished good code is required.');
    if (!fgName.trim()) return setErr('Finished good name is required.');
    if (existingCodes.includes(fgCode.trim())) return setErr(`A BOM for ${fgCode} already exists.`);
    const components = rows
      .filter((r) => r.code && Number(r.qtyPer) > 0)
      .map((r) => ({ code: r.code, qtyPer: Number(r.qtyPer), uom: itemByCode[r.code]?.uom || 'nos' }));
    if (components.length === 0) return setErr('Add at least one component with a quantity.');
    onSave({ fg: fgCode.trim(), name: fgName.trim(), outputUom: fgUom.trim() || 'nos', components });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>New Bill of Materials</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Finished Good</Typography>
          <TextField label="Finished Good Code" value={fgCode} onChange={(e) => setFgCode(e.target.value)} size="small" fullWidth helperText="e.g. 301089-1" />
          <TextField label="Finished Good Name" value={fgName} onChange={(e) => setFgName(e.target.value)} size="small" fullWidth helperText="e.g. LATCH HOUSING OPN 30" />
          <TextField label="Output Unit (UoM)" value={fgUom} onChange={(e) => setFgUom(e.target.value)} size="small" fullWidth />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Components (per unit)</Typography>
            <Button size="small" startIcon={<Plus size={14} />} onClick={addRow} sx={{ textTransform: 'none' }}>Add</Button>
          </Box>

          {rows.map((r, idx) => (
            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              {hasItems ? (
                <TextField select label="Component" value={r.code} onChange={(e) => setRow(idx, { code: e.target.value })} size="small" sx={{ flex: 1 }}>
                  {inventory.map((i) => (
                    <MenuItem key={i.code} value={i.code}>{i.code} — {i.name}</MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField label="Component Code" value={r.code} onChange={(e) => setRow(idx, { code: e.target.value })} size="small" sx={{ flex: 1 }} />
              )}
              <TextField
                label={`Qty${hasItems && itemByCode[r.code] ? ` (${itemByCode[r.code].uom})` : ''}`}
                type="number" value={r.qtyPer} onChange={(e) => setRow(idx, { qtyPer: e.target.value })}
                size="small" sx={{ width: 130 }}
              />
              <IconButton size="small" onClick={() => removeRow(idx)} sx={{ mt: 0.5, color: '#be123c' }} disabled={rows.length === 1}>
                <Trash2 size={16} />
              </IconButton>
            </Box>
          ))}

          {err && <Typography sx={{ fontSize: 12.5, color: '#be123c' }}>{err}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button onClick={submit} variant="contained" sx={{ textTransform: 'none' }}>Save BOM</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Bom() {
  const { data, loading, error, save, reload } = useErp(KEYS);
  const [dialogOpen, setDialogOpen] = useState(false);

  const boms = data?.boms || [];
  const inventory = data?.inventory || [];

  const fail = () => Swal.fire({ icon: 'error', title: 'Save failed', text: 'Could not save to ThingsBoard. Please retry.', confirmButtonColor: '#5b3df5' });
  const handleSave = (bom) => {
    save('boms', [...boms, bom]).catch(fail);
  };
  const handleDelete = (fg) => {
    const bom = boms.find((b) => b.fg === fg);
    Swal.fire({
      title: 'Delete this BOM?',
      html: `<b>${bom ? bom.name : fg}</b> (${fg})<br/><span style="font-size:13px;color:#64748b">Production orders using it will lose their material plan.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: 'text.disabled',
      confirmButtonText: 'Yes, delete it',
      allowOutsideClick: false,
    }).then((result) => {
      if (!result.isConfirmed) return;
      save('boms', boms.filter((b) => b.fg !== fg))
        .then(() => Swal.fire({ icon: 'success', title: 'Deleted', timer: 1200, showConfirmButton: false }))
        .catch(fail);
    });
  };

  const totalComponents = boms.reduce((s, b) => s + (b.components ? b.components.length : 0), 0);

  return (
    <PageShell>
      <PageHeader
        title="Bill of Materials"
        subtitle="Define finished goods and the components consumed to produce them"
        actions={<Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => setDialogOpen(true)}>New BOM</Button>}
      />

      <BomDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        inventory={inventory}
        existingCodes={boms.map((b) => b.fg)}
      />

      <LoadGate loading={loading} error={error} onRetry={reload}>
        <Box sx={{ mb: 2.5 }}>
          <StatStrip
            items={[
              { label: 'BOMs', value: String(boms.length), tone: 'primary' },
              { label: 'Total Components', value: String(totalComponents), tone: 'sky' },
              { label: 'Inventory Items', value: String(inventory.length), tone: 'success', hint: 'available to use' },
            ]}
          />
        </Box>

        {boms.length === 0 ? (
          <Panel title="Bills of Materials" accent="linear-gradient(90deg,#7c5cff,#818cf8)">
            <Typography sx={{ p: 4, textAlign: 'center', color: 'text.disabled', fontSize: 14 }}>
              No BOMs yet. Click <strong>New BOM</strong> to define a finished good and its components — it’s saved to ThingsBoard and becomes selectable when creating a Production Order.
            </Typography>
          </Panel>
        ) : (
          <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
            {boms.map((b) => (
              <Panel
                key={b.fg}
                title={b.name}
                subtitle={`${b.fg} · output in ${b.outputUom || 'nos'}`}
                accent="linear-gradient(90deg,#7c5cff,#818cf8)"
                actions={
                  <IconButton size="small" onClick={() => handleDelete(b.fg)} sx={{ color: '#be123c' }}>
                    <Trash2 size={16} />
                  </IconButton>
                }
              >
                <Box sx={{ px: 2, pb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                    <Layers size={14} color="#64748b" />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                      {b.components ? b.components.length : 0} component(s) per unit
                    </Typography>
                  </Box>
                  {(b.components || []).map((c) => (
                    <Box key={c.code} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.primary' }}>{c.code}</Typography>
                        {(() => {
                          const item = inventory.find((i) => i.code === c.code);
                          return item ? <Typography component="span" sx={{ fontSize: 13, ml: 1 }}>{item.name}</Typography> : null;
                        })()}
                      </Box>
                      <StatusChip label={`${formatNumber(c.qtyPer, c.qtyPer % 1 ? 2 : 0)} ${c.uom}`} tone="secondary" />
                    </Box>
                  ))}
                </Box>
              </Panel>
            ))}
          </Box>
        )}
      </LoadGate>
    </PageShell>
  );
}
